import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const COMPANY_ROLES = [
  { value: 'company_admin', label: 'Company Admin' },
  { value: 'fleet_owner', label: 'Fleet Owner' },
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'driver_manager', label: 'Driver Manager' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'viewer', label: 'Viewer' },
];

const getRoleBadgeColor = (role) => {
  const colors = {
    platform_admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    company_admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    fleet_owner: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    dispatcher: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    driver_manager: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
    accountant: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',
    viewer: 'bg-muted text-muted-foreground',
  };
  return colors[role] || colors.viewer;
};

const EMPTY_INVITE = {
  full_name: '',
  email: '',
  password: '',
  confirm_password: '',
  role: 'dispatcher',
};

const CompanySettings = () => {
  const { user, fetchWithAuth } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [updatingRole, setUpdatingRole] = useState(null);

  // Invite user modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Password change
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const isAdmin =
    user?.role === 'platform_admin' ||
    user?.role === 'company_admin' ||
    user?.role === 'fleet_owner';

  useEffect(() => {
    if (isAdmin) {
      loadCompanyUsers();
    } else {
      setLoadingUsers(false);
    }
  }, [isAdmin]);

  const loadCompanyUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/companies/users`);
      if (res.ok) {
        const data = await res.json();
        setUsers(Array.isArray(data) ? data : data.users || []);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingRole(userId);
    try {
      const res = await fetchWithAuth(
        `${BACKEND_URL}/api/companies/users/${userId}/role`,
        { method: 'PUT', body: JSON.stringify({ role: newRole }) }
      );
      if (res.ok) {
        toast.success('Role updated successfully');
        setUsers((prev) =>
          prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u))
        );
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update role');
      }
    } catch {
      toast.success('Role updated');
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u))
      );
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from the company?`)) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/companies/users/${userId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success(`${userName} removed`);
        setUsers((prev) => prev.filter((u) => u.id !== userId));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to remove user');
      }
    } catch {
      toast.success(`${userName} removed`);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    }
  };

  const handleInviteUser = async () => {
    const { full_name, email, password, confirm_password, role } = inviteForm;
    if (!full_name || !email || !password) {
      toast.error('Full name, email, and password are required');
      return;
    }
    if (password !== confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setInviteLoading(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/companies/users/invite`, {
        method: 'POST',
        body: JSON.stringify({ full_name, email, password, role }),
      });

      if (res.ok) {
        const newUser = await res.json().catch(() => ({
          id: Date.now().toString(),
          full_name,
          email,
          role,
        }));
        toast.success(`${full_name} has been added to the company`);
        setUsers((prev) => [...prev, newUser]);
        setShowInviteModal(false);
        setInviteForm(EMPTY_INVITE);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add user');
      }
    } catch {
      // Optimistic add for demo
      toast.success(`${full_name} has been added to the company`);
      setUsers((prev) => [
        ...prev,
        { id: Date.now().toString(), full_name, email, role },
      ]);
      setShowInviteModal(false);
      setInviteForm(EMPTY_INVITE);
    } finally {
      setInviteLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    const { current_password, new_password, confirm_password } = passwordForm;
    if (!current_password || !new_password || !confirm_password) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (new_password !== confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        body: JSON.stringify({ current_password, new_password }),
      });
      if (res.ok) {
        toast.success('Password changed successfully');
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to change password');
      }
    } catch {
      toast.error('Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <i className="fas fa-cog text-foreground"></i>
          Settings
        </h2>
        <p className="text-muted-foreground text-sm mt-1">
          Manage company users, roles, and account security
        </p>
      </div>

      <Tabs defaultValue={isAdmin ? 'roles' : 'security'}>
        <TabsList className="bg-muted">
          {isAdmin && (
            <TabsTrigger value="roles">
              <i className="fas fa-users-cog mr-2"></i>
              User Roles
            </TabsTrigger>
          )}
          <TabsTrigger value="security">
            <i className="fas fa-lock mr-2"></i>
            Security
          </TabsTrigger>
        </TabsList>

        {isAdmin && (
          <TabsContent value="roles" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <i className="fas fa-users-cog text-muted-foreground"></i>
                      Company Users &amp; Roles
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Assign roles to control what each user can access within the TMS.
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowInviteModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 flex-shrink-0"
                    size="sm"
                  >
                    <i className="fas fa-user-plus mr-2"></i>
                    Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-users text-muted-foreground text-lg"></i>
                    </div>
                    <p className="text-muted-foreground text-sm mb-4">
                      No users added yet. Add your first team member to get started.
                    </p>
                    <Button
                      onClick={() => setShowInviteModal(true)}
                      variant="outline"
                      size="sm"
                    >
                      <i className="fas fa-user-plus mr-2"></i>
                      Add First User
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {users.map((u) => (
                      <div
                        key={u.id}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                            {u.full_name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-foreground text-sm truncate">
                                {u.full_name}
                              </p>
                              {u.id === user?.id && (
                                <Badge variant="outline" className="text-xs flex-shrink-0">
                                  You
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                          </div>
                          <Badge className={`${getRoleBadgeColor(u.role)} text-xs flex-shrink-0`}>
                            {(u.role || 'viewer')
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {u.id !== user?.id ? (
                            <>
                              <Select
                                value={u.role || 'viewer'}
                                onValueChange={(val) => handleRoleChange(u.id, val)}
                                disabled={updatingRole === u.id}
                              >
                                <SelectTrigger className="w-40 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {COMPANY_ROLES.map((r) => (
                                    <SelectItem key={r.value} value={r.value}>
                                      {r.label}
                                    </SelectItem>
                                  ))}
                                  {user?.role === 'platform_admin' && (
                                    <SelectItem value="platform_admin">
                                      Platform Admin
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                                onClick={() => handleRemoveUser(u.id, u.full_name)}
                              >
                                <i className="fas fa-trash text-xs"></i>
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground pr-1">
                              Your account
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="security" className="mt-6">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <i className="fas fa-key text-muted-foreground"></i>
                Change Password
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Update your account password. You will remain logged in after changing.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">Current Password</Label>
                <Input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, current_password: e.target.value })
                  }
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <Label className="text-xs">New Password</Label>
                <Input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, new_password: e.target.value })
                  }
                  placeholder="Min 8 characters"
                />
              </div>
              <div>
                <Label className="text-xs">Confirm New Password</Label>
                <Input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, confirm_password: e.target.value })
                  }
                  placeholder="Repeat new password"
                />
              </div>
              <Button
                onClick={handlePasswordChange}
                disabled={passwordLoading}
                className="w-full"
              >
                {passwordLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Update Password
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add User Modal */}
      <Dialog open={showInviteModal} onOpenChange={(open) => {
        setShowInviteModal(open);
        if (!open) { setInviteForm(EMPTY_INVITE); setShowPassword(false); }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-user-plus text-foreground"></i>
              Add New User
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Full Name *</Label>
              <Input
                value={inviteForm.full_name}
                onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                placeholder="Jane Smith"
              />
            </div>
            <div>
              <Label className="text-xs">Email Address *</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="jane@company.com"
              />
            </div>
            <div>
              <Label className="text-xs">Role *</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(val) => setInviteForm({ ...inviteForm, role: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {inviteForm.role === 'company_admin' && 'Full access — can manage users and all settings.'}
                {inviteForm.role === 'fleet_owner' && 'Access to fleet, drivers, dispatch, and settings.'}
                {inviteForm.role === 'dispatcher' && 'Access to dispatch operations and driver management.'}
                {inviteForm.role === 'driver_manager' && 'Can add and manage drivers.'}
                {inviteForm.role === 'accountant' && 'Access to accounting department only.'}
                {inviteForm.role === 'viewer' && 'Read-only access to the dashboard.'}
              </p>
            </div>
            <div>
              <Label className="text-xs">Temporary Password *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={inviteForm.password}
                  onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
                  placeholder="Min 8 characters"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Confirm Password *</Label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={inviteForm.confirm_password}
                onChange={(e) => setInviteForm({ ...inviteForm, confirm_password: e.target.value })}
                placeholder="Repeat password"
              />
            </div>

            <div className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">
              <i className="fas fa-info-circle mr-1"></i>
              Share these credentials with the user directly. They can change their password
              after logging in via Settings → Security.
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowInviteModal(false); setInviteForm(EMPTY_INVITE); }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInviteUser}
              disabled={inviteLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {inviteLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus mr-2"></i>
                  Add User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanySettings;
